import { createFeatureSelector, createSelector, select } from '@ngrx/store';
import { pipe } from 'rxjs';
import { filter } from 'rxjs/operators';

import { getJobsEntities } from '@app/collections/jobs';
import { getUsersEntities } from '@app/collections/users';
import {
  getCompaniesEntities,
  getCompanyWithAccountManager,
} from '@collections/companies/companies.selectors';
import { Company, isTruthy } from '@shared';

import { COMPANIES_PAGE_TOKEN, CompaniesPageState } from './companies-page.reducer';

export const getCompaniesPageState =
  createFeatureSelector<CompaniesPageState>(COMPANIES_PAGE_TOKEN);

export const getCompaniesPage = createSelector(
  getCompaniesPageState,
  (state: CompaniesPageState) => !!state && state.companies,
);

export const getCompaniesPageDetails = createSelector(
  getCompaniesPageState,
  (state: CompaniesPageState) => !!state && state.pageDetails,
);

export const getCompaniesPageLoading = createSelector(
  getCompaniesPageState,
  (state: CompaniesPageState) => !!state && state.pageDetails.loading,
);

export const getJobsPerCompany = createSelector(
  getCompaniesPageState,
  (state: CompaniesPageState) => !!state && state.jobsPerCompany,
);

export const getCompaniesPageIsLoaded = createSelector(
  getCompaniesPageState,
  (state: CompaniesPageState) => !!state && state.pageDetails.loaded,
);

export const getCompaniesFromSelectedPage = createSelector(
  getCompaniesPageState,
  (state: CompaniesPageState) => !!state && state.companies && state.companies.content,
);

export const getJobsFromSelectedPage = createSelector(
  getCompaniesPageState,
  (state: CompaniesPageState) => !!state && state.jobsPerCompany.content,
);

export const getCompaniesOnlyForPage = createSelector(
  getCompaniesFromSelectedPage,
  getCompaniesEntities,
  (ids, entities) =>
    !!ids && !!entities && ids.map((company: { id: number }) => entities[company.id]),
);

export const getJobsOnlyForPage = createSelector(
  getJobsFromSelectedPage,
  getJobsEntities,
  (ids, entities) => !!ids && !!entities && ids.map((job: { id: number }) => entities[job.id]),
);

export const getCompaniesPageWithAccountManagers = createSelector(
  getCompaniesOnlyForPage,
  getUsersEntities,
  (companies, users): Company[] =>
    !!companies &&
    !!users &&
    companies.map((company) => ({
      ...company,
      accountManager: users[company.accountManagerId],
    })),
);

export const getCompaniesPageFilter = createSelector(
  getCompaniesPageState,
  (state: CompaniesPageState) => !!state && state.filter,
);

export const getCompaniesPageSelectedCompanyWithAccountManagers = pipe(
  select(getCompanyWithAccountManager),
  filter(isTruthy),
);

export const getManagersPerCompany = createSelector(
  getCompaniesPageState,
  (state) => !!state && state.managersPerCompany.list,
);

export const getManagerByIdPerCompany = createSelector(
  getCompaniesPageState,
  (state, props) =>
    !!state.managersPerCompany.list.content &&
    state.managersPerCompany.list.content.find(({ id }) => id === parseInt(props.id, 10)),
);
